import {
  ACCESS_ADD_ON_TYPE,
  DynamicPriceRate,
  FeeLine,
  toDynamicPriceRate,
} from "@/offering/api";
import type { AddOnOrderParams } from "@/offering/order_params";
import { toFeeLine } from "@/offering/hooks/use_quote_query";
import { toNumber } from "@/lib/js_utils/number_utils";
import { addHours } from "../../offering/time_utils";
import { Location, BaseLocation } from "./location";
import { SpaceType } from "@/data";
import { RecurringBooking, toRecurringBooking } from "./recurring_booking";

enum TemplateName {
  DayPass = "DayPass",
  MultiPass = "MultiPass",
  EmbedDayPass = "EmbedDayPass",
  EmbedDayOffice = "EmbedDayOffice",
  EmbedMeetingRoom = "EmbedMeetingRoom",
  MultiDayOffice = "MultiDayOffice",
}

export interface OrderInput {
  fullName: string;
  phoneNumber: string;
  email: string;
  startDate?: string;
  endDate?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  expirationDate?: string;
  quoteId?: string;
  ignorePayment?: boolean;
  companyName?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  taxNumber?: string;
  addOns?: AddOnOrderParams[];
}

export enum OrderStatus {
  Created = "created",
  Paid = "paid",
  Canceled = "canceled",
  Confirmed = "confirmed",
  Accepted = "accepted",
  Chargeback = "chargeback",
  Rejected = "rejected",
  DisputePending = "dispute_pending",
  DisputeWon = "dispute_won",
  DisputeLost = "dispute_lost",
  PaymentFailed = "payment_failed",
  Requested = "requested",
}

export enum OrderableType {
  Booking = "Booking",
  AddOn = "AddOn",
  Membership = "Membership",
}

export enum BookingType {
  Hourly = "Hourly",
  Daily = "Daily",
}

enum DiscountType {
  FixedPrice = "fixed_price",
  Amount = "amount",
  Percentage = "percentage",
  OriginalPrice = "original_price",
}

export enum OrderViewOption {
  ByUsageDate = "usageDate",
  ByCreatedDate = "createdDate",
}

export enum AccessableType {
  OrderLineItem = "OrderLineItem",
  Subscription = "Subscription",
}

interface OrderItemPrice {
  id: string;
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  totalPrice: number;
  dynamicPriceRate?: DynamicPriceRate;
}

interface OrderItemBase {
  ID: string;
  quantity: number;
  orderableID: string;
  orderID: string;
  orderItemID: string;
  locationID: string;
  orderConfirmationID: string;
  orderName: string;
  orderCurrency: string;
  orderEmail: string;
  orderTotalPrice: number;
  orderTotalCumulativePrice: number | null;
  subscriptionFirstPaymentAmount: number;
  productType: {
    id: string;
    name: string;
  };
  status: OrderStatus;
  createdAt: Date;
  orderMarketingConsent?: boolean;
  dynamicPriceRate?: DynamicPriceRate;
  originalUnitPrice: number;
  originalSubTotal: number;
  unitPrice: number;
  subTotal: number;
  promotionCode?: string | null;
  accessableType?: AccessableType;
  accessableId?: string;
  orderItemPrices?: OrderItemPrice[];
  feeLines?: FeeLine[];
  /**
   * The recurring booking this line belongs to, and its 1-based position in it.
   *
   * `occurrenceIndex` is a stable label, not a position: it is assigned at quote
   * in ascending date order and never renumbered, so cancelling the third date
   * leaves the rest as 1, 2, 4, 5. Both are absent on every order today — see
   * `data/recurring_booking.ts` for the payload this is waiting on.
   */
  bookingSeriesId?: string;
  occurrenceIndex?: number;
  // The one day an add-on line was bought for. Set only on add-on items, which
  // the API writes one of per selected date, so a multi-day booking whose add-on
  // covers a subset of its days arrives as one item per covered day. Booking and
  // membership items leave it undefined.
  spaceOfferingDate?: string;
}

export interface OrderItemDayPass extends OrderItemBase {
  type: OrderableType.Booking;
  inventoryExceededBy: number;
  details: OrderItemDayPassDetails;
  orderable: {
    id?: string;
    spaceType: SpaceType;
  };
}

export interface OrderItemHourlyPass extends OrderItemBase {
  type: OrderableType.Booking;
  details: OrderItemHourlyPassDetails;
  orderable: {
    id?: string;
    spaceType: SpaceType;
  };
}

interface OrderItemHourlyPassDetails {
  type: BookingType.Hourly;
  date: string;
  hours: number;
  startTime: string;
  endTime: string;
  spaceID: string;
  attendees?: number;
  accessID?: string;
  accessName?: string;
  accessNumberOfEntries?: string;
  layoutID: string;
  eventTypeName?: string;
  eventTypeDescription?: string;
  status?: BookingStatus;
  /**
   * Whether the booking can be released while its order stays paid. Computed by
   * the backend, which alone can see the workspace-system config and the
   * booking's siblings on the line. The action is hidden when false; the
   * endpoint returns the specific reason if it is attempted anyway.
   */
  cancellableWithoutRefund?: boolean;
  /**
   * Partner-facing names of the workspace systems this booking currently syncs
   * to, so the cancel warning can say which sync it severs. Empty when the
   * booking has no live sync — there is then nothing to warn about.
   */
  bookingIntegrationCancellations?: string[];
}

interface OrderItemDayPassDetails {
  type: BookingType.Daily;
  startDate: string;
  endDate: string;
  spaceID: string;
  layoutID: string;
  days: number;
  physicalSpaceID?: string;
  physicalSpace?: {
    id: string;
    floor: string;
    name: string;
  };
  attendees?: number;
  accessID?: string;
  accessName?: string;
  accessNumberOfEntries?: string;
  eventTypeName?: string;
  eventTypeDescription?: string;
  status?: BookingStatus;
  /**
   * Whether the booking can be released while its order stays paid. Computed by
   * the backend, which alone can see the workspace-system config and the
   * booking's siblings on the line. The action is hidden when false; the
   * endpoint returns the specific reason if it is attempted anyway.
   */
  cancellableWithoutRefund?: boolean;
  /**
   * Partner-facing names of the workspace systems this booking currently syncs
   * to, so the cancel warning can say which sync it severs. Empty when the
   * booking has no live sync — there is then nothing to warn about.
   */
  bookingIntegrationCancellations?: string[];
}

export function isDayPass(
  item: OrderItemDayPass | OrderItemHourlyPass
): item is OrderItemDayPass {
  return item.details.type === BookingType.Daily;
}

export function isHourlyPass(
  item: OrderItemDayPass | OrderItemHourlyPass
): item is OrderItemHourlyPass {
  return item.details.type === BookingType.Hourly;
}

export enum BookingStatus {
  Created = "created",
  Cancelled = "canceled",
}

// Backend normalizes legacy "none" sentinel to null.
export type AmendedStatus =
  | "partially_refunded"
  | "extended"
  | "modified"
  | null;

// sub_transaction_type, stamped on every payment_transactions row at write
// time (RFC: "Stamp refund kind on sub_transaction_type across all refund
// writers"). It spans both charge and refund rows — `transactionType`
// disambiguates the values that appear on both sides (adhoc, modification).
//   refund rows: adhoc | modification | order_cancellation
//                | subscription_refund | subscription_proration_refund
//                | dispute | dispute_fee
//   charge rows: order_payment | membership_first_payment
//                | membership_recurring_payment | adhoc | modification
//                | pms_invoice | dispute_reversal
// Nullable only during the deploy/backfill window, or if a writer omits the
// kwarg — the API contract types it `.optional`.
export type TransactionKind =
  | "adhoc"
  | "modification"
  | "order_cancellation"
  | "subscription_refund"
  | "subscription_proration_refund"
  | "dispute"
  | "dispute_fee"
  | "order_payment"
  | "membership_first_payment"
  | "membership_recurring_payment"
  | "pms_invoice"
  | "dispute_reversal";

// "Additional" = an adjustment to a live order with no other representation in
// the order model (adhoc charge/refund). Other refund kinds are surfaced via
// amendedStatus / order.status / the subscription endpoint instead.
// Backend invariant: amount === subTotal + taxAmount + feeAmount for both
// charges and refunds. feeAmount is an aggregate (per-fee names not exposed).
interface AdditionalAdjustmentBase {
  // Amounts are in the order's currency cents (integer).
  amount: number;
  subTotal: number;
  taxAmount: number;
  feeAmount: number;
  currency: string;
  createdAt: Date;
  note: string | null;
  cardBrand: string | null;
  cardEnding: string | null;
}

export type AdditionalRefundTransaction = AdditionalAdjustmentBase;
export type AdditionalChargeTransaction = AdditionalAdjustmentBase;

function toAdditionalAdjustment(data: any): AdditionalAdjustmentBase {
  return {
    amount: data.amount,
    subTotal: data.sub_total,
    taxAmount: data.tax_amount,
    feeAmount: data.fee_amount,
    currency: data.currency,
    // Backend sends unix seconds, matching the rest of orders.ts (see toOrderItem).
    createdAt: new Date(data.created_at * 1000),
    note: data.note ?? null,
    cardBrand: data.card_brand ?? null,
    cardEnding: data.card_ending ?? null,
  };
}

const toAdditionalRefundTransaction = toAdditionalAdjustment;
const toAdditionalChargeTransaction = toAdditionalAdjustment;

export enum LocatableType {
  Location = "Location",
  GeoMarket = "GeoMarket",
  Partner = "SpacePartner",
}

interface GeoMarket {
  id: string;
  name: string;
  description: string;
  locations: Location[];
}

interface MembershipOrderable {
  id: string;
  name: string;
  partnerID: string;
  locatableType: LocatableType;
  locatable?: Location | GeoMarket;
  timezone: string;
  currency: string;
  monthlyPrice: number;
  description: string;
  productCategory: string;
}

function toMembershipOrderable(data: any): MembershipOrderable {
  let locatable: Location | GeoMarket | undefined;
  if (!data || !data.locatable) {
    locatable = undefined;
  } else if (data.locatable_type === LocatableType.Location) {
    locatable = {
      ...(data.locatable || {}),
      city: data.locatable?.city,
      currencyCode: data.locatable?.currency,
      streetAddress: data.locatable?.street_address,
    };
  } else if (data.locatable_type === LocatableType.GeoMarket) {
    locatable = {
      id: data.id,
      name: data.locatable.name,
      description: data.locatable.description!,
      locations: data.locatable.locations.map((location: any) => ({
        ...(location || {}),
        city: location?.city,
        currencyCode: location?.currency,
        streetAddress: location?.street_address,
      })),
    };
  }

  return {
    id: data.id,
    name: data.name,
    partnerID: data.partner_id,
    currency: data.currency,
    description: data.description,
    locatable,
    locatableType: data.locatable_type,
    timezone: data.time_zone,
    monthlyPrice: Number(data.monthly_price),
    productCategory: data.product_category,
  };
}

export interface OrderItemMembership extends OrderItemBase {
  type: OrderableType.Membership; // TODO verify this membership type
  inventoryExceededBy: number;
  orderable?: MembershipOrderable;
  // The subscription this membership purchase created, used to deep-link from
  // the order detail page to the membership detail page. Absent for orders
  // still awaiting approval (no subscription exists yet).
  subscriptionId?: string;
  // The membership's effective start (subscription.start_date) — can be a
  // future date when the buyer chose a later start. Shown on the order view
  // so staff can tell members when access begins without opening the
  // membership. Absent until the subscription exists.
  subscriptionStartDate?: string;
  // Names of PMS integrations (Nexudus, OfficeRnD) whose membership-side
  // cancel will be triggered as a downstream effect of cancelling this order.
  // Populated by the backend only when the cancel will actually propagate to
  // the PMS — i.e. the subscription has a stripe_subscription_id so Stripe
  // emits customer.subscription.deleted, which drives SubscriptionIntegrationCancel.
  // Empty for non-recurring memberships and memberships with no PMS attachment.
  subscriptionIntegrationCancellations?: string[];
}

export enum AddOnType {
  MultiPass = "MultiPass",
}

interface OrderItemMultiPassDetails {
  type: AddOnType.MultiPass;
  addOnType: string;
  spaceID: string;
  addOnID: string;
  name: string;
  description: string;
  numberOfEntries: number;
  daysToExpire: number;
}

export interface OrderItemMultiPass extends OrderItemBase {
  type: OrderableType.AddOn;
  details: OrderItemMultiPassDetails;
  orderable: {
    productCategory: string;
  };
}

export interface CreditCard {
  last4: string;
  network: string;
  networkLogoUrl: string | null;
}

export interface PaymentMethod {
  card: CreditCard | null;
}

// Re-export the canonical APIError so callers using `usePartnerPost` get the
// same error shape as `usePost`. Removes the duplicate empty subclass that
// previously lived here.
export { APIError } from "@/data/api";

export interface OrderBillingDetails {
  fullName?: string;
  phone?: string;
  email?: string;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  tax: string;
}

export interface TaxLine {
  amount: number;
  percentage: number;
  description: string;
}

export interface PromotionLine {
  amount: number;
  discount: number;
  itemPriceAfterDiscount: number;
  discountType: DiscountType;
  description: string;
  code: string;
}

export type OrderItem =
  | OrderItemDayPass
  | OrderItemHourlyPass
  | OrderItemMultiPass
  | OrderItemMembership;

export interface OrderLineItem {
  id: string;
  locationId: string;
  orderableType: OrderableType;
  usageDate?: string;
  productType: {
    id: string;
    name: string;
  };
  addOnType: string;
  membershipPlanPortal: boolean;
  subscriptionId: string;
  details: { spaceID?: string; startDate?: string; endDate?: string };
  orderItemPrices: OrderItemPrice[];
  parentLineItemId: string | null;
  /** See the same pair on `OrderItemBase`. Absent on every order today. */
  bookingSeriesId?: string;
  occurrenceIndex?: number;
}

export interface Order {
  id: string;
  currencyCode: string;
  subTotal: number;
  subTotalBeforeDiscount: number;
  subscriptionFirstPaymentAmount: number;
  totalPrice: number;
  totalPriceInUsd: number;
  stripeClientSecret: string;
  confirmationID: string;
  additionalNotes: string;
  status: OrderStatus;
  orderItems: OrderItem[];
  stripePaymentIntentId?: string;
  fullName: string;
  companyName: string;
  email: string;
  bookingReason: string;
  inviteID: string;
  phoneNumber: string;
  paidThrouTeamsSubscription: boolean;
  paymentMethodDetails: PaymentMethod | null;
  billingDetails: OrderBillingDetails | null;
  createdAt: Date;
  cancelledAt: string;
  taxLine: TaxLine | null;
  promotionLine: PromotionLine | null;
  source?: string;
  sourceLabel?: string;
  cancellable: boolean;
  uncancellableReason?: string;
  cancellationLockedUntil?: string | null;
  quoteId: string;
  currency: string;
  amendedOrders?: AmendedOrder[];
  feeLines?: FeeLine[];
  specialInstructions?: string;
  refundableAmount: number;
  // Chain-level total the cancel flow will actually refund (root + all
  // paid/confirmed amendments). Only present on the order-detail endpoint;
  // fall back to refundableAmount for single-order payloads.
  chainRefundableAmount?: number;
  rejectedReason?: string;
  partnerOfferingID?: string;
  amendedStatus: AmendedStatus;
  // Always present; backend returns [] when none exist. Both are adhoc-only
  // and scoped to this order (not chain-aggregated) — callers aggregate the
  // amendment chain themselves.
  additionalRefundTransactions: AdditionalRefundTransaction[];
  additionalChargeTransactions: AdditionalChargeTransaction[];
  /**
   * The recurrence rule, when this order is a recurring booking: one order, one
   * payment, N booking lines. Undefined on every other order — and on every
   * order today, until the serializer carries it.
   */
  recurringBooking?: RecurringBooking;
  /**
   * The kinds of room this order could be switched to beyond its own (meeting
   * room <-> event space), computed server-side so the pairing rule keeps a
   * single home. Empty when the order's kind has no compatible pair — the room
   * picker then keeps its same-type list. Only present on the order-detail
   * endpoint; absent payloads read as "cannot cross".
   */
  compatibleSpaceTypes?: string[];
  /** Only on the root order of a membership checkout that collected a signature. */
  signedContract?: SignedContract | null;
}

export interface SignedContract {
  signerName: string;
  /** ISO 8601, already in the membership's time zone and carrying its offset. */
  signedAt: string;
  /** Null until the signed PDF has been built. */
  downloadUrl: string | null;
}

export function toSignedContract(data: any): SignedContract | null {
  return data
    ? {
        signerName: data.signer_name,
        signedAt: data.signed_at,
        downloadUrl: data.download_url ?? null,
      }
    : null;
}

export interface AmendedOrder extends Order {
  orderItems: OrderItemHourlyPass[];
}

// Single source for the amount a cancel dialog promises: cancellation
// refunds the whole amendment chain, so prefer the chain-level total when
// the payload carries it. The fallback keeps payloads without the field
// rendering their own per-order amount rather than $0.
export function getCancelRefundPreviewAmount(order: Order): number {
  return order.chainRefundableAmount ?? order.refundableAmount;
}

export type PayableType = "Order" | "Subscription";

export interface OrderPaymentTransaction {
  id: string;
  totalPrice: number;
  currency: string;
  transactionType: "charge" | "refund";
  // sub_transaction_type from the DB — stamped on every payment_transactions
  // row at write time (see TransactionKind). Null only during the backfill
  // window or when a writer omits the kwarg.
  subTransactionType: TransactionKind | null;
  note: string | null;
  payableType: PayableType | null;
  cardBrand: string;
  cardEnding: string;
  createdAt: string;
  cardNetworkUrl: string;
  orderId: string;
  payableId: string | null;
  isRecurringSubscriptionCharge: boolean;
  // Billing period of the charge this row belongs to; a dispute reports the
  // period of the charge it disputes. Null for a first payment.
  periodStart: string | null;
  periodEnd: string | null;
}

export const toOrderPaymentTransaction = (
  data: any
): OrderPaymentTransaction => {
  const totalPrice = toNumber(data.total_price);
  return {
    id: data.id,
    totalPrice: Number.isFinite(totalPrice) ? totalPrice : 0,
    currency: data.currency,
    transactionType: data.transaction_type,
    subTransactionType: data.sub_transaction_type ?? null,
    note: data.note ?? null,
    payableType: data.payable_type ?? null,
    cardBrand: data.card_brand,
    cardEnding: data.card_ending,
    createdAt: data.created_at,
    cardNetworkUrl: data.card_network_url,
    orderId: data.order_id,
    payableId: data.payable_id ?? null,
    isRecurringSubscriptionCharge:
      data.is_recurring_subscription_charge ?? false,
    periodStart: data.period_start ?? null,
    periodEnd: data.period_end ?? null,
  };
};

export type TaxInvoiceDocumentType = "invoice" | "credit_note";
export type TaxInvoiceStatus = "active" | "voided";

/**
 * One invoice on an order, whichever system issued it.
 *
 * `issuedAt` is nullable: a provider-issued document carries no date on the
 * payload the backend stores. Sort accordingly; never dereference it.
 */
export type InvoiceSource = "green_invoice" | "flexspace";

export interface OrderInvoice {
  id: string;
  /** The order this invoice was listed from — its download route is nested under it. */
  orderId: string;
  invoiceNumber: string;
  documentType: TaxInvoiceDocumentType;
  status: TaxInvoiceStatus;
  issuedAt: string | null;
  paymentTransactionId: string;
  /**
   * Which system issued it. Carried because the row says so, not because
   * anything branches on it — the backend decides where a download comes from.
   */
  source: InvoiceSource;
}

export const toOrderInvoice = (
  data: Record<string, unknown>,
  orderId: string
): OrderInvoice => ({
  id: data.id as string,
  orderId,
  invoiceNumber: data.invoice_number as string,
  documentType: data.document_type as TaxInvoiceDocumentType,
  status: data.status as TaxInvoiceStatus,
  issuedAt: (data.issued_at as string | null) ?? null,
  paymentTransactionId: data.payment_transaction_id as string,
  source: data.source as InvoiceSource,
});

export interface MultiPassAccess {
  id: string;
  createdAt: number; // unix timestamp in sec
  numberOfEntries: number;
  expired: boolean; // false
  name: string;
  expirationDate: string;
  bookingsUsedCount: number;
  bookingsUnusedCount: number;

  // location space
  space: {
    id: string;
    name: string;
    thumbnailImage: string;
  };

  // location
  location: {
    id: string;
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };

  // orders
  order: {
    id: string;
    confirmationID: string;
    fullname: string;
    email: string;
    currency: string;
    quoteId: string;
  };

  // bookings
  bookings: MultiPassAccessBooking[];
}

export interface MultiPassAccessBooking {
  id: string;
  date: string;
  past: boolean; // in the future
  orderConfirmationID: string;
  orderID: string;
}

export interface CreateMultiPassBookingInput {
  date: string;
}

export interface PartnerUserProfile {
  userID: string;
  partnerID: string;
  partnerLocations: BaseLocation[];
  partnerName: string;
  partnerSlug: string | null;
  partnerOfferings: { id: string; templateName: TemplateName }[];
}

export interface OrderItemData {
  id: string;
  confirmationId: string;
  totalPrice: number;
  currency: string;
  status: OrderStatus;
  fullname: string;
  email: string;
  createdAt: Date;
  promotionCode: string;
  /** What the promotion took off, as a positive amount; 0 where none applied. */
  promotionAmount: number;
  subscriptionFirstPaymentAmount: number;
  totalCumulativePrice: number;
  orderLineItems: OrderLineItem[];
  amendedOrders?: OrderItemData[];
}

export interface OrdersSummary {
  ordersCount: number;
  /** How many of the counted orders are a recurring booking. */
  recurringOrdersCount: number;
  totalRevenue: number;
  totalTax: number;
  partnerHasTaxConfigured: boolean;
  currency: string;
}

export interface ManageOrderItemsData {
  orders?: OrderItemData[];
  paging: {
    currentPage: number;
    totalPage: number;
    perPage: number;
    total: number;
  };
  summary?: OrdersSummary;
  loading?: boolean;
}

export interface BookingsParam {
  startDate?: string;
  endDate?: string;
  viewOrderBy?: string;
  productType?: string;
  page?: number;
  perPage?: number;
  searchText?: string;
  locationID?: string;
  orderStatuses?: OrderStatus[];
  /** The "Recurring only" chip: keep only orders that are a recurring booking. */
  recurring?: boolean;
}

export interface OrdersSummaryParam {
  startDate?: string;
  endDate?: string;
  viewOrderBy: string;
  productType?: string;
  searchText?: string;
  orderStatuses?: OrderStatus[];
  recurring?: boolean;
}

export function fromOrderInput(input: OrderInput) {
  return {
    fullname: input.fullName,
    phone: input.phoneNumber,
    email: input.email,
    expiration_date: input.expirationDate,
    quote_id: input.quoteId,
    ignore_payment: input.ignorePayment,
    company_name: input.companyName,
    street_address: input.streetAddress,
    city: input.city,
    state: input.state,
    country: input.country,
    postal_code: input.postalCode,
    tax_number: input.taxNumber,
  };
}

export function toMultiPassAccess(data: any): MultiPassAccess {
  return {
    id: data.id,
    createdAt: data.created_at,
    numberOfEntries: data.number_of_entries,
    expired: data.expired,
    name: data.name,
    expirationDate: data.expiration_date,
    bookingsUsedCount: data.bookings_used_count,
    bookingsUnusedCount: data.bookings_unused_count,
    space: {
      id: data.space.id,
      name: data.space.name,
      thumbnailImage: data.space.thumbnail_image,
    },
    location: {
      id: data.location.id,
      name: data.location.name,
      streetAddress: data.location.street_address,
      city: data.location.city,
      state: data.location.state,
      postalCode: data.location.postal_code,
      country: data.location.country,
    },
    order: {
      id: data.order.id,
      confirmationID: data.order.confirmation_id,
      fullname: data.order.fullname,
      email: data.order.email,
      currency: data.order.currency,
      quoteId: data.order.quote_id,
    },
    bookings: data.bookings.map(toMultiPassAccessBooking),
  };
}

function toMultiPassAccessBooking(data: any) {
  return {
    id: data.id,
    date: data.date,
    past: data.past,
    orderConfirmationID: data.order_confirmation_id,
    orderID: data.order_id,
  };
}

export function toOrderItemDayPassDetails(data: any): OrderItemDayPassDetails {
  return {
    type: BookingType.Daily,
    attendees: data?.attendees,
    layoutID: data?.space_layout_id,
    spaceID: data?.space_id,
    startDate: data?.start_date,
    endDate: data?.end_date,
    days: data?.days,
    physicalSpace: data?.physical_space,
    physicalSpaceID: data?.physical_space_id,
    accessID: data?.access_id,
    accessName: data?.access_name,
    accessNumberOfEntries: data?.access_number_of_entries,
    eventTypeName: data?.event_type_name,
    eventTypeDescription: data?.event_type_description,
    status: data?.status,
    cancellableWithoutRefund: data?.cancellable_without_refund,
    bookingIntegrationCancellations:
      data?.booking_integration_cancellations ?? [],
  };
}

export function toPartnerUserProfile(data: any) {
  return {
    userID: data.auth0_user_id,
    partnerID: data.partner_id,
    partnerLocations: data.partner_locations.map(toPartnerOfferingLocation),
    partnerName: data.partner_name,
    partnerInternalName: data.partner_internal_name,
    partnerSlug: data.partner_slug,
    partnerOfferings: data.partner_offerings.map(toPartnerOffering),
  };
}

function toPartnerOffering(data: any) {
  return {
    id: data.id,
    templateName: data.template_name,
  };
}

function toPartnerOfferingLocation(data: any) {
  return {
    id: data.location_id,
    name: data.location_name,
    streetAddress: data.location_street_address,
    city: data.location_city,
    timezone: data.location_time_zone,
    promoCodeBoxContent: data.promo_code_box_content,
    featuredPromoCode: data.featured_promo_code,
    generalInfo: data.general_info,
    live: data.live,
  };
}

function toOrderItemPrice(data: any): OrderItemPrice {
  return {
    id: data.id,
    startDate: data.start_date,
    endDate: data.end_date,
    startTime: data.start_time,
    endTime: data.end_time,
    totalPrice: data.total_price,
    dynamicPriceRate: data.dynamic_price_rate
      ? toDynamicPriceRate(data.dynamic_price_rate)
      : undefined,
  };
}

/**
 * A recurring booking's dates are labelled 1..N, so 0 is not a value the column
 * can hold — anything non-positive is treated as absent rather than rendered as
 * a "#0" row.
 */
function toOccurrenceIndex(value: unknown): number | undefined {
  const index = Number(value);
  return Number.isInteger(index) && index > 0 ? index : undefined;
}

function toOrderItem(data: any): OrderItem {
  const type: OrderableType = data.type || data.orderable_type;

  const base = {
    ID: data.id,
    orderableID: data.orderable_id,
    orderID: data.order_id,
    orderItemID: data.order_item_id,
    locationID: data.location_id,
    orderConfirmationID: data.order_confirmation_id,
    orderName: data.order_name,
    status: data.order_status,
    quantity: data.quantity,
    orderTotalPrice: data.order_total_price,
    orderTotalCumulativePrice: data.total_cumulative_price,
    subscriptionFirstPaymentAmount: data.subscription_first_payment_amount,
    orderEmail: data.order_email,
    orderCurrency: data.order_currency,
    productType: {
      id: data.product_type?.id,
      name: data.product_type?.name,
    },
    createdAt: new Date(data.created_at * 1000),
    orderMarketingConsent: !!data.order_marketing_consent,
    dynamicPriceRate: data.dynamic_price_rate
      ? toDynamicPriceRate(data.dynamic_price_rate)
      : undefined,
    originalUnitPrice: data.original_unit_price,
    originalSubTotal: data.original_sub_total,
    unitPrice: data.unit_price,
    subTotal: data.sub_total,
    promotionCode: data.promotion_code,
    accessableId: data.accessable_id,
    accessableType: data.accessable_type,
    orderItemPrices: data.order_item_prices?.map(toOrderItemPrice),
    feeLines: data.fee_lines?.map(toFeeLine),
    bookingSeriesId: data.booking_series_id ?? undefined,
    occurrenceIndex: toOccurrenceIndex(data.occurrence_index),
    spaceOfferingDate: data.space_offering_date ?? undefined,
  };

  if (type === OrderableType.AddOn) {
    return {
      ...base,
      type,
      details: toOrderItemMultiPassDetails(data.details),
      orderable: {
        productCategory: data.orderable.product_category,
      },
    };
  }

  if (type === OrderableType.Membership) {
    return {
      ...base,
      inventoryExceededBy: data.inventory_exceeded_by,
      orderable: data.orderable
        ? toMembershipOrderable(data.orderable)
        : undefined,
      subscriptionIntegrationCancellations:
        data.subscription_integration_cancellations ?? [],
      subscriptionId: data.subscription_id ?? undefined,
      subscriptionStartDate: data.subscription_start_date ?? undefined,
      type,
    };
  }

  if (data.details?.start_time) {
    return {
      ...base,
      type,
      details: toOrderItemHourlyPassDetails(data.details),
      orderable: {
        id: data.orderable?.id,
        spaceType: data.orderable.space_type,
      },
    };
  }

  if (data.details?.time) {
    return {
      ...base,
      type,
      details: toLegacyOrderItemHourlyPassDetails(data.details),
      orderable: {
        id: data.orderable?.id,
        spaceType: data.orderable.space_type,
      },
    };
  }

  return {
    ...base,
    type,
    unitPrice: data.unit_price,
    subTotal: data.sub_total,
    inventoryExceededBy: data?.inventory_exceeded_by,
    details: toOrderItemDayPassDetails(data.details),
    orderable: {
      id: data.orderable?.id,
      spaceType: data.orderable.space_type,
    },
  };
}

function toOrderItemV2(data: any): OrderLineItem {
  return {
    id: data.id,
    productType: {
      id: data.product_type?.id,
      name: data.product_type?.name,
    },
    locationId: data.location_id,
    usageDate: data.usage_date,
    orderableType: data.orderable_type,
    addOnType: data.details?.add_on_type,
    membershipPlanPortal: data.orderable?.membership_plan_portal,
    subscriptionId: data.subscription_id,
    details: {
      spaceID: data.details?.space_id,
      startDate: data.details?.start_date,
      endDate: data.details?.end_date,
    },
    orderItemPrices: data.order_item_prices?.map(toOrderItemPrice),
    parentLineItemId: data.parent_line_item_id ?? null,
    bookingSeriesId: data.booking_series_id ?? undefined,
    occurrenceIndex: toOccurrenceIndex(data.occurrence_index),
  };
}

export function toManageOrders(
  manageOrdersResponse: any
): ManageOrderItemsData {
  return {
    orders: manageOrdersResponse.data.map(toOrderItemData),
    paging: {
      currentPage: manageOrdersResponse.paging?.current_page,
      totalPage: manageOrdersResponse.paging?.total_page,
      perPage: manageOrdersResponse.paging?.per_page,
      total: manageOrdersResponse.paging?.total,
    },
  };
}

export function toOrdersSummary(data: any): OrdersSummary {
  return {
    ordersCount: data.orders_count,
    recurringOrdersCount: data.recurring_orders_count ?? 0,
    totalRevenue: data.total_revenue,
    totalTax: data.total_tax,
    partnerHasTaxConfigured: data.partner_has_tax_configured,
    currency: data.currency,
  };
}

function toOrderItemData(order: any): OrderItemData {
  return {
    id: order.id,
    confirmationId: order.confirmation_id,
    totalPrice: order.total_price,
    currency: order.currency,
    status: order.status,
    fullname: order.fullname,
    email: order.email,
    createdAt: new Date(order.created_at * 1000),
    promotionCode: order.promotion_code,
    promotionAmount: order.promotion_amount ?? 0,
    subscriptionFirstPaymentAmount: order.subscription_first_payment_amount,
    totalCumulativePrice: order.total_cumulative_price,
    orderLineItems: order.order_line_items.map(toOrderItemV2),
    amendedOrders: order.amended_orders?.map(toOrderItemData),
  };
}

function toOrderItemMultiPassDetails(data: any): OrderItemMultiPassDetails {
  return {
    type: data?.type,
    addOnType: data?.add_on_type,
    spaceID: data?.space_id,
    addOnID: data?.add_on_id,
    name: data?.add_on_name,
    description: data?.add_on_description,
    numberOfEntries: data?.number_of_entries,
    daysToExpire: data?.days_to_expire,
  };
}

function toOrderItemHourlyPassDetails(data: any): OrderItemHourlyPassDetails {
  return {
    type: BookingType.Hourly,
    spaceID: data?.space_id,
    date: data?.date,
    hours: data?.hours,
    startTime: data?.start_time,
    endTime: data?.end_time,
    layoutID: data?.space_layout_id,
    attendees: data?.attendees,
    eventTypeName: data?.event_type_name,
    eventTypeDescription: data?.event_type_description,
    status: data?.status,
    cancellableWithoutRefund: data?.cancellable_without_refund,
    bookingIntegrationCancellations:
      data?.booking_integration_cancellations ?? [],
  };
}

function toLegacyOrderItemHourlyPassDetails(
  data: any
): OrderItemHourlyPassDetails {
  return {
    type: BookingType.Hourly,
    spaceID: data?.space_id,
    date: data?.date,
    hours: data?.hours,
    startTime: data?.time,
    endTime: addHours(data?.time, data?.duration),
    layoutID: data?.space_layout_id,
    status: data?.status,
    cancellableWithoutRefund: data?.cancellable_without_refund,
    bookingIntegrationCancellations:
      data?.booking_integration_cancellations ?? [],
  };
}

export function toOrder(data: any): Order {
  return {
    id: data.id,
    confirmationID: data.confirmation_id,
    status: data.status,
    stripePaymentIntentId: data.stripe_payment_intent_id,
    subTotal: data.sub_total,
    subTotalBeforeDiscount: data.sub_total_before_discount,
    subscriptionFirstPaymentAmount: data.subscription_first_payment_amount,
    additionalNotes: data.additional_notes,
    currencyCode: data.currency,
    stripeClientSecret: data.stripe_client_secret,
    totalPrice: data.total_price,
    totalPriceInUsd: data.total_price_in_usd,
    fullName: data.fullname,
    email: data.email,
    bookingReason: data.booking_reason,
    companyName: data.company_name,
    inviteID: data.invite_id,
    phoneNumber: data.phone,
    paidThrouTeamsSubscription: data.paid_throu_teams_subscription,
    billingDetails: data.billing_details
      ? {
          fullName: data.billing_details.full_name,
          phone: data.billing_details.phone,
          email: data.billing_details.email,
          streetAddress: data.billing_details.street_address,
          city: data.billing_details.city,
          state: data.billing_details.state,
          postalCode: data.billing_details.postal_code,
          country: data.billing_details.country,
          tax: data.billing_details.tax_number,
        }
      : null,
    orderItems: data.order_items.map(toOrderItem),
    paymentMethodDetails: toPaymentMethod(data.payment_method_details),
    createdAt: new Date(data.created_at * 1000),
    taxLine: data.tax_line
      ? {
          amount: data.tax_line.amount,
          description: data.tax_line.description,
          percentage: data.tax_line.percentage,
        }
      : null,
    promotionLine: data.promotion_line
      ? {
          amount: data.promotion_line.amount,
          description: data.promotion_line.description,
          code: data.promotion_line.code,
          discount: data.promotion_line.discount,
          discountType: data.promotion_line.discount_type,
          itemPriceAfterDiscount: data.promotion_line.item_price_after_discount,
        }
      : null,
    source: data.source,
    sourceLabel: data.source_label,
    cancellable: data.cancellable,
    uncancellableReason: data.uncancellable_reason,
    cancellationLockedUntil: data.cancellation_locked_until ?? null,
    cancelledAt: data.cancelled_at,
    quoteId: data.quote_id,
    currency: data.currency,
    amendedOrders: data.amended_orders?.map(toOrder),
    feeLines: data.fee_lines?.map(toFeeLine),
    specialInstructions: data.special_instructions,
    refundableAmount: data.refundable_amount,
    chainRefundableAmount: data.chain_refundable_amount ?? undefined,
    rejectedReason: data.rejected_reason,
    partnerOfferingID: data.partner_offering_id,
    amendedStatus: data.amended_status ?? null,
    recurringBooking: toRecurringBooking(data.recurring_booking),
    additionalRefundTransactions:
      data.additional_refund_transactions?.map(toAdditionalRefundTransaction) ??
      [],
    additionalChargeTransactions:
      data.additional_charge_transactions?.map(toAdditionalChargeTransaction) ??
      [],
    compatibleSpaceTypes: data.compatible_space_types ?? [],
    signedContract: toSignedContract(data.signed_contract),
  };
}

// Why the issuer refused a charge. `code` names the class of failure
// ("card_declined"); `declineCode` the issuer's specific reason
// ("insufficient_funds"). Only the pair separates "ask them to top up" from
// "ask them for a different card", and either may be absent — Stripe omits
// what the issuer did not say.
export interface PaymentDeclineReason {
  code: string | null;
  declineCode: string | null;
  message: string | null;
}

// The API answers this beside the generic payment_failed code, never in place
// of it, so an older API that omits it degrades to reason-less copy.
export function toPaymentDeclineReason(data: any): PaymentDeclineReason | null {
  if (!data) {
    return null;
  }

  return {
    code: data.code ?? null,
    declineCode: data.decline_code ?? null,
    message: data.message ?? null,
  };
}

function toPaymentMethod(data: any): PaymentMethod | null {
  if (!data || !data.card) {
    return null;
  }

  return {
    card: {
      last4: data.card.last4,
      network: data.card.network,
      networkLogoUrl: data.card.network_logo_url,
    },
  };
}

function getBookingOrderItem(order: Order) {
  const oi = order.orderItems.find(
    (oi) => "type" in oi && oi.type === OrderableType.Booking
  );

  return oi;
}

function getMembershipOrderItem(order: Order): OrderItem | undefined {
  const membershipOrder = order.orderItems.find(
    (oi) => oi.type === OrderableType.Membership
  );

  if (!membershipOrder) {
    return undefined;
  }

  return membershipOrder;
}

function getAddOnOrderItem(order: Order): OrderItemMultiPass | undefined {
  const oi = order.orderItems.find((oi) => oi.type === OrderableType.AddOn);

  if (oi?.type !== OrderableType.AddOn) {
    return undefined;
  }

  return oi;
}

export function isAccessAddOnItem(item: OrderItem): item is OrderItemMultiPass {
  return (
    item.type === OrderableType.AddOn &&
    item.details?.addOnType === ACCESS_ADD_ON_TYPE
  );
}

// Same ranking as the offering copy: a booking add-on never outranks the room
// booking it rides on, only a Multi Pass does.
export function getMainOrderItem(order: Order) {
  return (
    order.orderItems.find(isAccessAddOnItem) ||
    getBookingOrderItem(order) ||
    getAddOnOrderItem(order) ||
    getMembershipOrderItem(order)
  );
}

export function displayStatus(status: OrderStatus) {
  switch (status) {
    case OrderStatus.Canceled:
      return "Canceled";
    case OrderStatus.Confirmed:
      return "Confirmed";
    case OrderStatus.Accepted:
      return "Accepted";
    case OrderStatus.Paid:
      return "Paid";
    case OrderStatus.Created:
      return "Created";
    case OrderStatus.Chargeback:
      return "Chargeback";
    case OrderStatus.Rejected:
      return "Rejected";
    case OrderStatus.DisputePending:
      return "Dispute pending";
    case OrderStatus.DisputeWon:
      return "Dispute won";
    case OrderStatus.DisputeLost:
      return "Dispute lost";
    case OrderStatus.PaymentFailed:
      return "Payment failed";
    case OrderStatus.Requested:
      return "Requested";
    default:
      return "";
  }
}

interface CancelOrder {
  refundedAmount: number;
  currency: string;
  message: string;
  status: string;
  // Present (non-null) when the request was rejected by the invoicing lock.
  // Other failure modes return null.
  cancellationLockedUntil: string | null;
}

export function toPartnerCancelOrder(data: any): CancelOrder {
  return {
    refundedAmount: data.refunded_amount,
    currency: data.currency,
    message: data.message,
    status: data.status,
    cancellationLockedUntil: data.cancellation_locked_until ?? null,
  };
}
